import { useState, useEffect, useMemo, useRef } from "react";
import usePalette from "../../../hooks/usePalette";
import Box from "../../components-dev/BoxExtended";
import { XNGStandardTab } from "../../types/xngStandardTab";
import { ReactComponent as XLogsLogoSvg } from "../../svgs/logo.svg";
import TransparentTabs from "../../low-level/tabs_transparent";
import { CircularProgress, IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import DropdownIndicator from "../../low-level/dropdown_indicator";
import { XNGMenu, XNGMenuAnchorBox } from "../../components-dev/xng_menu";
import { XNGICONS, XNGIconRenderer } from "../../icons";
import MediaQueryBox from "../../components-dev/MediaQueryBox";
import { ROUTES_XLOGS } from "../../../constants/URLs";
import { getSizing } from "../../sizing";
import { BOX_SHADOWS } from "../../styles/boxShadow";
import NotificationsMenu from "./menus/notifications_menu";
import MainMenuContent from "./menus/main_menu";
import { INotification } from "./types";
import { selectClientID } from "../../../context/slices/loggedInClientSlice";
import {
  selectLoggedInClientAssignment,
  selectUser,
  ACTION_SetProxyCaseload,
} from "../../../context/slices/userProfileSlice";
import { placeholderForFutureLogErrorText } from "../../../temp/errorText";
import { selectStateInUS } from "../../../context/slices/stateInUsSlice";
import { breakpointUtils } from "../../../utils/breakpointUtils";
import XNGAvatar from "../../low-level/avatar";
import { useXNGDispatch, useXNGSelector } from "../../../context/store";
import { isAtLeastEmployee } from "../../../utils/msb_role";
import { ClientRef, PatchClientAssignmentRequest } from "../../../profile-sdk";
import { API_SERVICEPROVIDERS_BACKEND, API_USERS, API_USERS_BACKEND } from "../../../api/api";
import { CaseloadType } from "@xng/xng-backend";

import {
  selectDataEntryProvider,
  setDataEntryProvider,
} from "../../../context/slices/dataEntryProvider";
import { CurrentDataEntryProviderMenu } from "./menus/proxy_menu";
import { isLoggedInUserCookieExpired } from "../../../utils/cookies";
import usePath from "../../../hooks/use_path";
import logoutRedirect from "../../../utils/logout_redirect";
import useApiQueryUnpostedSessionsCount from "../../../views/unposted_sessions/hooks/api/use_api_query_unposted_sessions_count";
import useUserRoles from "../../../hooks/use_user_roles";
import { MSBSearchSingleSelect } from "../../../fortitude";
import { DotDotDot } from "../../components-dev/dot_dot_dot";
import { useClientSelectorDetails } from "./hooks/use_client_selector_details";
import { useInactivityTimer } from "../../../hooks/use_inactivity_timer";
import { InactivityWarningModal } from "../../../fortitude/components/inactivity_warning_modal";
import { API_NOTIFICATIONS } from "../../../api/api";
import type {
  GetNotificationsDistrictAccessRequestNotification,
  GetNotificationsProxyAccessRequestNotification,
  GetNotificationsResponseNotification,
} from "@xng/xng-backend";
import useApiQueryClientAssignments from "../../../api/hooks/clients/use_api_query_client_assignments";

export const NAVBAR_HEIGHT = getSizing(8);

function Navbar() {
  // REDUX SELECTORS
  const dataEntryProvider = useXNGSelector(selectDataEntryProvider);
  const reportingActive = useXNGSelector((state) => state.featureFlags.flags["ReportingActive"]);
  const myLearningActive = useXNGSelector((state) => state.featureFlags.flags["MyLearningActive"]);
  const msbAdminPortalActive = useXNGSelector(
    (state) => state.featureFlags.flags["MsbAdminPortalActive"],
  );
  const isFinancialDashboardFeatureAllowed = useXNGSelector(
    (state) => state.featureFlags.flags["FinancialDashboardActive"],
  );
  const loggedInClientAssignment = useXNGSelector(selectLoggedInClientAssignment);
  const isAdminUser =
    loggedInClientAssignment.isDelegatedAdmin || loggedInClientAssignment.isExecutiveAdmin;

  // HOOKS
  const palette = usePalette();
  const dispatch = useXNGDispatch();
  const selectedTabIndex = useSelectedTabIndexBasedOnPath();

  // SELECTORS
  const loggedInClientId = useXNGSelector(selectClientID);
  const userProfile = useXNGSelector(selectUser);
  const state = useXNGSelector(selectStateInUS);

  // MENUS STATES
  const [myProfileOpen, setMainMenuOpen] = useState<boolean>(false);
  const [myProfileAnchorEl, setMyProfileAnchorEl] = useState<HTMLElement | null>(null);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState<HTMLElement | null>(null);
  const [notificationsOpen, setNotificationsOpen] = useState<boolean>(false);
  const [dataEntryProviderOpen, setDataEntryProviderOpen] = useState<boolean>(false);
  const [dataEntryProviderAnchorEl, setActingServiceProviderAnchorEl] =
    useState<HTMLElement | null>(null);
  const [loadingNotifications, setLoadingNotifications] = useState<boolean>(false);
  //
  const firstNameInitial = dataEntryProvider?.firstName?.charAt(0) ?? "";
  const lastNameInitial = dataEntryProvider?.lastName?.charAt(0) ?? "";
  const actingServiceProviderInitials = firstNameInitial + lastNameInitial;

  const [inactivityModalOpen, setInactivityModalOpen] = useState<boolean>(false);

  const {
    timeRemainingMinutes,
    timeRemainingSeconds,
    resetTimer: resetInactivityTimer,
  } = useInactivityTimer({
    timeoutMinutes: 30,
    warningMinutes: 5,
    onWarning: () => {
      setInactivityModalOpen(true);
    },
    onTimeout: () => {
      logoutRedirect();
    },
  });

  useApiQueryUnpostedSessionsCount({
    queryParams: {
      state: state,
      userId: userProfile?.id ?? "",
      clientId: loggedInClientAssignment.client?.id ?? "",
    },
  });

  // API - NOTIFICATIONS //
  // -- State
  // const [notifications, setNotifications] = useState<INotification[]>([]);
  const [districtAccessRequestNotifications, setDistrictAccessRequestNotifications] = useState<
    GetNotificationsDistrictAccessRequestNotification[] | null | undefined
  >([]);
  const [proxyAccessRequestNotifications, setProxyAccessRequestNotifications] = useState<
    GetNotificationsProxyAccessRequestNotification[] | null | undefined
  >([]);
  const [responseNotifications, setResponseNotifications] = useState<
    GetNotificationsResponseNotification[] | null | undefined
  >([]);
  // -- API Request
  async function fetchAndSetNotifications() {
    if (isLoggedInUserCookieExpired()) {
      logoutRedirect();
    }

    // console.log("BEGINNING FETCH NOTIFICATIONS")
    const userServiceProviderId = loggedInClientAssignment?.serviceProviderProfile?.id;
    // console.log("userServiceProviderId: ", userServiceProviderId);
    const userId = userProfile?.id;
    // console.log("userId: ", userId);
    var districtIds;
    if (loggedInClientAssignment.isExecutiveAdmin || loggedInClientAssignment.isDelegatedAdmin) {
      // TODO: We'll need to update this to send all districtIds that the user is authorized in for this client.
      districtIds = loggedInClientAssignment?.authorizedDistricts?.map((ad) => ad.id).join(",");
    }
    // console.log("districtIds: ", districtIds);
    if (userServiceProviderId === undefined) throw Error(placeholderForFutureLogErrorText);
    if (userId === undefined) throw Error(placeholderForFutureLogErrorText);
    if (loggedInClientId === undefined) throw Error(placeholderForFutureLogErrorText);
    setLoadingNotifications(true);

    // needs role check, only admins should be sending district ids
    const response = await API_NOTIFICATIONS.getNotifications({
      userId: userId,
      serviceProviderId: userServiceProviderId,
      clientId: loggedInClientId,
      districtIds: loggedInClientAssignment?.authorizedDistricts?.map((ad) => ad.id).join(","),
    });

    const {
      districtAccessRequestNotifications,
      proxyAccessRequestNotifications,
      responseNotifications,
    } = response;

    setLoadingNotifications(false);

    setDistrictAccessRequestNotifications(districtAccessRequestNotifications);
    setProxyAccessRequestNotifications(proxyAccessRequestNotifications);
    setResponseNotifications(responseNotifications);
  }
  // -- Polling System
  useEffect(() => {
    fetchAndSetNotifications();
    const interval = setInterval(() => {
      if (isLoggedInUserCookieExpired()) {
        logoutRedirect();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Removed: reFetchUserOnNotificationUpdate was replacing the entire Redux user state
  // on every page load, causing a massive re-render cascade (74K+ App Insights track requests).
  // The JSON.stringify comparison always found differences due to date formatting / null vs
  // undefined in fresh API responses. Specific state changes (caseload, etc.) are handled
  // by targeted Redux actions (ACTION_SetProxyCaseload, etc.) instead.

  // -- reorganize later
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState<number>(0);
  useEffect(() => {
    setUnreadNotificationsCount(
      (districtAccessRequestNotifications ?? []).filter(
        (n: GetNotificationsDistrictAccessRequestNotification) =>
          n.isRead === undefined || n.isRead === null,
      ).length +
        (proxyAccessRequestNotifications ?? []).filter(
          (n: GetNotificationsProxyAccessRequestNotification) =>
            n.isRead === undefined || n.isRead === null,
        ).length +
        (responseNotifications ?? []).filter(
          (n: GetNotificationsResponseNotification) => n.isRead === undefined || n.isRead === null,
        ).length,
    );
  }, [districtAccessRequestNotifications, proxyAccessRequestNotifications, responseNotifications]);

  async function removeProviderFromProxyCaseload(serviceProviderId: string) {
    const spId = loggedInClientAssignment.serviceProviderProfile?.id;
    if (!spId) return;

    const memberUser = await API_USERS_BACKEND
      .getUserByServiceProvider({
        serviceProviderId,
        clientId: loggedInClientId!,
      });
    if (!memberUser.id) return;

    const updatedCaseload = await API_SERVICEPROVIDERS_BACKEND
      .deleteCaseload({
        serviceProviderId: spId,
        caseloadType: CaseloadType.NUMBER_2,
        memberId: memberUser.id,
      });

    const mappedProviders = updatedCaseload.map((r) => ({
      id: r.id,
      firstName: r.firstName,
      lastName: r.lastName,
      email: r.email,
    }));

    dispatch(
      ACTION_SetProxyCaseload({
        providers: mappedProviders,
        loggedinClientId: loggedInClientId!,
      }),
    );
  }

  const userRoles = useUserRoles();
  const isAuthorizedToViewFinancials = useMemo(
    () => userRoles.includes("Executive Admin"),
    [userRoles],
  );

  // TODO: Review this logic with Paul. Should we even read 'isSuperAdmin' or this an abstraction of
  // the user's assigned multiple roles as readable from the `loggedInClientAssignment`?
  const isSuperAdmin = userProfile?.isSuperAdmin;
  const isMSBAdmin = userProfile?.isMsbAdmin;
  const shouldShowAdminTab = useMemo(
    () =>
      loggedInClientAssignment.isDelegatedAdmin ||
      loggedInClientAssignment.isExecutiveAdmin ||
      userProfile?.isSuperAdmin ||
      userProfile?.isMsbAdmin,
    [isSuperAdmin, isMSBAdmin, userProfile],
  );

  const tabs = useMemo(() => {
    let res: XNGStandardTab[] = [
      {
        label: "calendar",
        navTo: ROUTES_XLOGS.calendar,
      },
      {
        label: "students",
        navTo: ROUTES_XLOGS._students.manager,
      },
    ];

    if (reportingActive) {
      res.push({
        label: "reports",
        navTo: ROUTES_XLOGS.reports.index,
      });
    }
    if (myLearningActive) {
      res.push({
      label: "My Learning",
      onClick: () => window.open("https://msbconnect.northpass.com", "_blank"),
    });
    }

    if (shouldShowAdminTab) {
      res.push({
        label: "admin",
        navTo:
          isFinancialDashboardFeatureAllowed && isAuthorizedToViewFinancials
            ? ROUTES_XLOGS._admin.mydistrictProfile
            : ROUTES_XLOGS._admin.index,
      });
    }

    // The MSB operator zone (XLOGS-205): msb_role only — district admins never
    // see it. MsbEmployee and above, behind the portal's own flag.
    if (isAtLeastEmployee(userProfile) && msbAdminPortalActive) {
      res.push({
        label: "MSB Admin Portal",
        navTo: ROUTES_XLOGS._msb.index,
      });
    }

    return res;
  }, [isAdminUser, shouldShowAdminTab, reportingActive, msbAdminPortalActive, userProfile]);

  const { selectableClientOptions, changeClientMutation, selectedClient, setSelectedClient } =
    useClientSelectorDetails();

  const freshClientAssignments = useApiQueryClientAssignments({
    queryParams: {
      id: userProfile?.id ?? "",
    },
  });

  const freshClientOptions = useMemo(
    () =>
      (freshClientAssignments.data?.map((ca) => ca.client as ClientRef) ?? []).slice().sort((a, b) =>
        (a.name ?? "").localeCompare(b.name ?? "", undefined, { sensitivity: "base" }),
      ),
    [freshClientAssignments.data],
  );

  // DOM HIERARCHY
  return (
    <>
      {/* MENUS */}

      <XNGMenu
        anchorEl={notificationsAnchorEl}
        open={notificationsOpen}
        onClose={() => setNotificationsOpen(false)}
        content={
          <NotificationsMenu
            notificationsIsLoading={loadingNotifications}
            onClose={() => setNotificationsOpen(false)}
            fetchAndSetNotifications={fetchAndSetNotifications}
            districtAccessRequestNotifications={districtAccessRequestNotifications}
            proxyAccessRequestNotifications={proxyAccessRequestNotifications}
            responseNotifications={responseNotifications}
          />
        }
      />
      <XNGMenu
        anchorEl={myProfileAnchorEl}
        open={myProfileOpen}
        onClose={() => setMainMenuOpen(false)}
        content={<MainMenuContent onClose={() => setMainMenuOpen(false)} />}
      />
      <XNGMenu
        anchorEl={dataEntryProviderAnchorEl}
        open={dataEntryProviderOpen}
        onClose={() => setDataEntryProviderOpen(false)}
        content={
          <CurrentDataEntryProviderMenu
            setShowMenu={(show) => setDataEntryProviderOpen(show)}
            currentDataEntryProvider={dataEntryProvider ?? {}}
            user={userProfile!}
            onSignOut={() => {
              dispatch(setDataEntryProvider(null));
              setDataEntryProviderOpen(false);
            }}
            onRemove={() => removeProviderFromProxyCaseload(dataEntryProvider!.id!)}
            onBackToProfile={() => {
              dispatch(setDataEntryProvider(null));
              setDataEntryProviderOpen(false);
            }}
          />
        }
      />

      {/* DOM */}
      <Box
        sx={{
          display: "flex",
          width: "100%",
          minHeight: NAVBAR_HEIGHT,
          ...BOX_SHADOWS[0],
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: getSizing(1),
            minWidth: breakpointUtils.greaterThan("sm") ? getSizing(40) : getSizing(10),
          }}
        >
          <XLogsLogo />

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              paddingTop: getSizing(0.5),
            }}
          >
            { (
              <Tooltip
                disableHoverListener
                arrow
                open={changeClientMutation.status === "pending"}
                title={
                  changeClientMutation.status === "pending" ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: ".5rem", p: ".25rem" }}>
                      <Typography variant="inherit">Logging into {selectedClient.name!}</Typography>
                      <CircularProgress
                        size="1rem"
                        sx={{ color: palette.contrasts[5] }}
                        variant="indeterminate"
                      />
                    </Box>
                  ) : (
                    ""
                  )
                }
              >
                <div>
                  <Box py=".4rem">
                    <MSBSearchSingleSelect<ClientRef>
                      autocompleteProps={{
                        sx: { minWidth: "10rem" },
                        disableClearable: true,
                        disabled: changeClientMutation.status === "pending",
                      }}
                      options={freshClientOptions ?? []}
                      selectedOption={selectedClient}
                      getOptionLabel={(option) => option.name ?? ""}
                      onChange={(newClient) => {
                        setSelectedClient(newClient);
                        changeClientMutation.mutate(newClient);
                      }}
                      label="Client"
                    />
                  </Box>
                </div>
              </Tooltip>
            )}
          </Box>
        </Box>
        <VerticalDivider />
        <TransparentTabs
          useDropdownBreakpoint={{
            breakpoint: 1200,
            selectedValue: selectedTabIndex,
          }}
          value={selectedTabIndex}
          tabs={tabs}
        />

        <Box
          sx={{
            marginLeft: "auto",
            display: "flex",
            height: "100%",
            alignItems: "center",
            paddingRight: getSizing(1),
            gap: getSizing(1),
          }}
        >
          <XNGMenuAnchorBox
            onClickSetAnchorEl={(el) => setNotificationsAnchorEl(el)}
            onClickSetOpen={() => setNotificationsOpen(true)}
            sx={{ display: "block" }}
          >
            <IconButton>
              {unreadNotificationsCount > 0 && (
                <NotificationsBellBadge>{unreadNotificationsCount}</NotificationsBellBadge>
              )}

              <XNGIconRenderer i={<XNGICONS.Bell />} color={palette.contrasts[1]} size="md" />
            </IconButton>
          </XNGMenuAnchorBox>

          {dataEntryProvider && (
            <XNGMenuAnchorBox
              onClickSetAnchorEl={(el) => setActingServiceProviderAnchorEl(el)}
              onClickSetOpen={() => setDataEntryProviderOpen(true)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: getSizing(1),
                cursor: "pointer",
                padding: getSizing(1),
              }}
            >
              <XNGAvatar useCheckDecoration text={actingServiceProviderInitials} />
              <DropdownIndicator open={dataEntryProviderOpen} />
            </XNGMenuAnchorBox>
          )}
          <XNGMenuAnchorBox
            onClickSetAnchorEl={(el) => setMyProfileAnchorEl(el)}
            onClickSetOpen={() => setMainMenuOpen(true)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: getSizing(1),
              cursor: "pointer",
              padding: getSizing(1),
            }}
          >
            <XNGAvatar
              variant="outline"
              text={userProfile!.firstName![0]! + userProfile!.lastName![0]!}
            />
            <DropdownIndicator open={myProfileOpen} />
          </XNGMenuAnchorBox>
        </Box>
      </Box>
      <InactivityWarningModal
        open={inactivityModalOpen}
        timeRemainingMinutes={timeRemainingMinutes}
        timeRemainingSeconds={timeRemainingSeconds}
        onStayActive={() => {
          setInactivityModalOpen(false);
          resetInactivityTimer();
        }}
        onLogout={() => {
          setInactivityModalOpen(false);
          logoutRedirect();
        }}
      />
    </>
  );
}

function VerticalDivider() {
  const palette = usePalette();

  return (
    <Box
      sx={{
        height: "100%",
        alignItems: "center",
        display: "flex",
        marginX: getSizing(3),
      }}
    >
      <Box sx={{ width: "1px", height: "50%", bgcolor: palette.contrasts[3] }} />
    </Box>
  );
}

export default Navbar;

function useSelectedTabIndexBasedOnPath() {
  const path = usePath();

  const [selectedTab, setSelectedTab] = useState<number>(0);

  useEffect(() => {
    const tabPathSegment = path[2];
    switch (tabPathSegment) {
      case "calendar":
        setSelectedTab(0);
        break;
      case "students":
        setSelectedTab(1);
        break;
      case "reports":
        setSelectedTab(2);
        break;
      case "admin":
        setSelectedTab(4);
        break;
    }
  }, [path]);

  return selectedTab;
}

function NotificationsBellBadge(props: Readonly<{ children: React.ReactNode }>) {
  const palette = usePalette();

  return (
    <Box
      sx={{
        position: "absolute",
        top: -3,
        right: -3,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        borderRadius: 999,
        bgcolor: palette.danger[2],
        color: palette.contrasts[5],
        px: ".4rem",
        zIndex: 10,
      }}
    >
      <Typography variant="subtitle1">{props.children}</Typography>
    </Box>
  );
}

function XLogsLogo() {
  const theme = useTheme();

  return (
    <>
      <MediaQueryBox
        sx={{ display: "flex", alignItems: "center" }}
        showIf={theme.breakpoints.down("md")}
      >
        <XNGICONS.XLogs_X />
      </MediaQueryBox>
      <MediaQueryBox
        sx={{
          minWidth: "140px",
          maxWidth: "280px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
        showIf={theme.breakpoints.up("md")}
      >
        <XLogsLogoSvg />
      </MediaQueryBox>
    </>
  );
}
